import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Eraser, Save, PenLine, CheckCircle2, Truck, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COMPANY_INFO = {
  name: 'NJK Logistics Pte Ltd',
  address: '212A Punggol Walk #12-741 Singapore 821212',
  phone: '98366186',
  email: 'inquiry@njklogistics.com',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-SG', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const PadCard = ({ title, hint, defaultName, captured, capturedName, capturedAt, capturedLorry, showLorry, defaultLorry, onSubmit, submitting }) => {
  const padRef = useRef(null);
  const [name, setName] = useState(defaultName || '');
  const [lorryNo, setLorryNo] = useState(defaultLorry || '');

  if (captured) {
    return (
      <Card className="border-green-200 bg-green-50/40">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={18} className="text-green-600" />
            <h3 className="font-semibold text-gray-800">{title}</h3>
          </div>
          <div className="bg-white border rounded-md p-2 mb-2 flex justify-center">
            <img src={captured} alt={`${title} signature`} className="max-h-24 object-contain" />
          </div>
          <p className="text-sm text-gray-700"><span className="text-gray-500">Name:</span> <strong>{capturedName}</strong></p>
          {showLorry && capturedLorry && <p className="text-sm text-gray-700"><span className="text-gray-500">Lorry No:</span> <strong>{capturedLorry}</strong></p>}
          <p className="text-sm text-gray-700"><span className="text-gray-500">Signed:</span> {fmtDate(capturedAt)}</p>
        </CardContent>
      </Card>
    );
  }

  const handleClear = () => padRef.current?.clear();
  const handleSubmit = async () => {
    console.log('[Signature] Submit clicked for', title);
    if (!padRef.current || padRef.current.isEmpty()) {
      toast.error('Please sign before submitting');
      return;
    }
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    let dataUrl;
    try {
      // Try trimmed canvas first (cleaner output)
      dataUrl = padRef.current.getTrimmedCanvas().toDataURL('image/png');
    } catch (err) {
      // Fallback: some mobile browsers (Safari iOS) crash on getTrimmedCanvas with the alpha lib
      try {
        dataUrl = padRef.current.toDataURL('image/png');
      } catch (err2) {
        toast.error('Could not capture signature. Please clear and try again.');
        console.error('Signature capture failed:', err, err2);
        return;
      }
    }
    if (!dataUrl || dataUrl.length < 100) {
      toast.error('Signature appears empty. Please try again.');
      return;
    }
    await onSubmit(dataUrl, name.trim(), lorryNo.trim());
  };

  return (
    <Card className="border-2 border-dashed border-orange-200">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <PenLine size={18} className="text-orange-600" />
          <h3 className="font-semibold text-gray-800">{title}</h3>
        </div>
        {hint && <p className="text-xs text-gray-500 mb-3">{hint}</p>}
        <Label className="text-sm">Name *</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          className="mt-1 mb-3"
          data-testid={`public-${title.toLowerCase().replace(/\W/g, '-')}-name`}
        />
        {showLorry && (
          <>
            <Label className="text-sm">Lorry Number</Label>
            <Input
              value={lorryNo}
              onChange={(e) => setLorryNo(e.target.value)}
              placeholder="e.g. XD1234A"
              className="mt-1 mb-3"
              data-testid="public-driver-lorry-no"
            />
          </>
        )}
        <Label className="text-sm">Sign in the box</Label>
        <div className="border-2 border-gray-300 rounded-md bg-white mt-1 mb-3 touch-none">
          <SignatureCanvas
            ref={padRef}
            penColor="#1f2937"
            clearOnResize={false}
            canvasProps={{ className: 'w-full h-32 rounded-md' }}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={handleClear} disabled={submitting}>
            <Eraser size={14} className="mr-1" /> Clear
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Save size={14} className="mr-1" /> {submitting ? 'Saving...' : 'Submit signature'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function PublicSignDeliveryOrder() {
  const { token } = useParams();
  const [doData, setDoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchDO = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/delivery-orders/sign/${token}`);
      setDoData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'This signing link is invalid or has been revoked.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDO();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const captureGps = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: Number(pos.coords.latitude.toFixed(6)),
          lng: Number(pos.coords.longitude.toFixed(6)),
          accuracy: Math.round(pos.coords.accuracy || 0),
        }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 6000, maximumAge: 60000 }
      );
    });

  const submitSignature = async (role, dataUrl, name, lorryNo) => {
    setSubmitting(true);
    try {
      const gps = await captureGps();
      const payload =
        role === 'driver'
          ? { driver_signature: dataUrl, driver_signature_name: name, driver_lorry_no: lorryNo || '' }
          : { customer_signature: dataUrl, customer_signature_name: name };
      if (gps) payload.gps = gps;
      const res = await axios.patch(
        `${BACKEND_URL}/api/public/delivery-orders/sign/${token}`,
        payload
      );
      toast.success(`${role === 'driver' ? 'Driver' : 'Customer'} signature recorded`);
      if (res.data?.both_signed) {
        toast.success('Both parties signed — confirmation emailed.', { duration: 5000 });
      }
      await fetchDO();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading delivery order...</p>
      </div>
    );
  }

  if (error || !doData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold text-red-600 mb-2">Link Unavailable</h2>
            <p className="text-gray-600">{error || 'This signing link is not valid.'}</p>
            <p className="text-sm text-gray-500 mt-4">Please contact NJK Logistics at {COMPANY_INFO.phone}.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const bothSigned = !!doData.driver_signature && !!doData.customer_signature;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="public-sign-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-orange-600">{COMPANY_INFO.name}</h1>
          <p className="text-sm text-gray-500">{COMPANY_INFO.address}</p>
          <p className="text-sm text-gray-500">Tel: {COMPANY_INFO.phone} · {COMPANY_INFO.email}</p>
        </div>

        {/* DO summary */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Truck className="text-orange-600" /> Delivery Order
                </h2>
                <p className="text-2xl font-bold text-orange-600 mt-1">{doData.document_number}</p>
              </div>
              {bothSigned && (
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                  ✓ Fully Signed
                </span>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4 text-sm mb-4 border-t pt-4">
              <div>
                <p className="text-gray-500 mb-1">Customer</p>
                <p className="font-semibold">{doData.company_name || doData.customer_name}</p>
                {doData.company_name && <p>{doData.customer_name}</p>}
              </div>
              <div>
                <p className="text-gray-500 mb-1 flex items-center gap-1"><Clock size={14} /> Delivery</p>
                <p className="font-semibold">{doData.delivery_date} {doData.delivery_time}</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1 flex items-center gap-1"><MapPin size={14} /> Pickup</p>
                <p>{doData.pickup_address || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1 flex items-center gap-1"><MapPin size={14} /> Delivery To</p>
                <p>{doData.delivery_address}</p>
              </div>
            </div>

            {/* Line items — pricing intentionally hidden from public DO view */}
            <table className="w-full text-sm border-collapse mt-2">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Description</th>
                  <th className="border p-2 text-center w-20">Qty</th>
                  <th className="border p-2 text-center w-20">Hrs</th>
                </tr>
              </thead>
              <tbody>
                {(doData.line_items || []).map((it, i) => (
                  <tr key={`li-${i}`}>
                    <td className="border p-2">{it.description}</td>
                    <td className="border p-2 text-center">{it.quantity}</td>
                    <td className="border p-2 text-center">{it.hours || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {doData.notes && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-xs text-gray-500 mb-1">Special Instructions</p>
                <p className="text-sm whitespace-pre-line">{doData.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signature pads */}
        <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <PenLine size={20} className="text-orange-600" /> E-Signatures
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <PadCard
            title="Driver / Delivered By"
            hint="Driver, please sign here first."
            defaultName={doData.driver_name}
            captured={doData.driver_signature}
            capturedName={doData.driver_signature_name}
            capturedAt={doData.driver_signed_at}
            capturedLorry={doData.driver_lorry_no}
            showLorry
            defaultLorry={doData.driver_lorry_no || doData.vehicle_number}
            onSubmit={(url, n, lorry) => submitSignature('driver', url, n, lorry)}
            submitting={submitting}
          />
          <PadCard
            title="Received By Customer"
            hint="Customer, please sign to confirm delivery."
            defaultName=""
            captured={doData.customer_signature}
            capturedName={doData.customer_signature_name}
            capturedAt={doData.customer_signed_at}
            onSubmit={(url, n) => submitSignature('customer', url, n)}
            submitting={submitting}
          />
        </div>

        {bothSigned && (
          <Card className="mt-6 bg-green-50 border-green-200">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="mx-auto text-green-600 mb-2" size={36} />
              <h3 className="font-bold text-green-800 mb-1">Thank you — signing complete!</h3>
              <p className="text-sm text-gray-600">
                A signed copy has been emailed to {doData.customer_email || 'the customer'}.
              </p>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-gray-400 mt-8">
          Powered by NJK Logistics Pte Ltd
        </p>
      </div>
    </div>
  );
}
